<template>
	<div class="checkout-page-container">
		<div class="navbar-white">
			<Navbar class="desktop-center" :logoSrc="$logoSrc" />
		</div>
		<div class="checkout-page-content desktop-center">
			<div class="checkout-content sk-scrollbar" ref="checkoutContent">
				<CheckoutVoucherCta v-if="showVoucherCta" />
				<div class="checkout-title">Review and place order</div>
				<div class="sk-widget-title">Delivery Information</div>
				<div class="checkout-subtitle">
					Ensure your order finds you as fast as possible!
				</div>
				<DefaultContactForm
					ref="defaultContactForm"
					:container="$refs.checkoutContent"
					:paymentMethod="paymentMethodSelected"
				/>
				<CheckoutDeliveryModeSection />
				<div class="sk-widget-title">How would you like to pay?</div>
				<div class="checkout-subtitle">
					How would you like to pay? Choose from multiple convenient
					options!
				</div>
				<PaymentOptions
					ref="paymentOptions"
					v-model="paymentMethodSelected"
					:options="paymentMethods"
					@methodSelected="paymentMethodClicked"
				/>
				<form id="payment-form" ref="cardOption">
					<div id="card-container"></div>
					<button
						id="card-button"
						type="button"
						style="display: none"
					></button>
				</form>
				<div id="payment-status-container"></div>
				<div
					class="sk-widget-alt in-room-billing-info"
					v-show="showInRoomBillingForm"
				>
					<div>
						<div class="in-room-billing-info-header">
							<div class="in-room-billing-info-icon">
								<i class="sk-icon-info-solid"></i>
							</div>
							<span>
								Confirm who and where to charge the order
							</span>
						</div>
						<div class="sk-divider-wrapper">
							<div class="divider"></div>
						</div>
						<div class="sk-checkbox-wrapper">
							<SkCheckbox v-model="useDeliveryInfo" />
							<span>Same As Delivery Information</span>
						</div>
						<div class="alternate-contact-info">
							<SkInput
								ref="alternateFirstNameInput"
								:name="'First Name on Reservation'"
								:required="true"
								v-model="alternateFirstName"
								data-cy="alternate-contact-form-first-name"
								@input="useDeliveryInfo = false"
							/>
							<SkInput
								ref="alternateLastNameInput"
								:name="'Last Name on Reservation'"
								:required="true"
								v-model="alternateLastName"
								data-cy="alternate-contact-form-last-name"
								@input="useDeliveryInfo = false"
							/>
						</div>
						<SkInput
							ref="alternateRoomInput"
							:name="'Room to Charge'"
							:required="true"
							v-model="alternateRoom"
							data-cy="alternate-contact-form-location"
							@input="useDeliveryInfo = false"
						/>
					</div>
				</div>
				<CheckoutCartSummary
					v-if="isMobile"
					@cartSummaryClicked="openCartSummary"
				/>
				<CheckoutAdditionalInfo v-if="isMobile" />
			</div>
			<FloatingCartSummary
				ref="cartSummary"
				@checkout="checkoutClicked"
			/>
			<Footer @checkout="checkoutClicked" />
		</div>
		<ItemModal ref="itemModal" />
	</div>
</template>

<style scoped>
.checkout-page-content {
	display: flex;
	height: calc(100% - 75px);
}

.special-voucher {
	margin: 25px 0 0 0 !important;
}

.sk-widget-alt {
	overflow: visible;
}

.checkout-title {
	font-size: 20px;
	font-weight: 600;
	color: var(--gR-base-90);
	margin-top: 30px;
	margin-bottom: 36px;
}

.sk-widget-title {
	margin-bottom: 0;
}

.checkout-content {
	margin: 0 23px;
	padding-left: 2px;
	padding-right: 2px;
	flex: 1;
	overflow-y: auto;
	overflow-x: hidden;
}

.checkout-page-container >>> .alcohol-disclaimer {
	align-items: center;
	background: var(--sk-grey-bg);
	padding: 15px;
	margin: 20px 0;
	border-radius: 5px;
	border: 1px solid var(--gR-white2);
	font-size: 10px;
}

.checkout-page-container >>> .alcohol-disclaimer i {
	margin-right: 20px;
}

.checkout-content >>> .checkout-additional-info {
	padding: 0;
	padding-bottom: 60px;
}

.checkout-cc-form-container {
	margin: -15px;
	background-color: var(--sk-grey-bg);
}

.alternate-contact-info {
	display: flex;
	margin-bottom: 14px;
}

.alternate-contact-info div:last-child {
	margin-left: 14px;
}

.in-room-billing-info .sk-divider-wrapper {
	margin: 17px 0 23px -15px;
	width: calc(100% + 30px);
}

.in-room-billing-info-header {
	display: flex;
	align-items: center;
}

.in-room-billing-info-header > span {
	font-size: 12px;
	font-weight: 600;
	color: var(--gR-base-80);
}

.in-room-billing-info-icon {
	margin-right: 10px;
	width: 15px;
	height: 15px;
	background: var(--gR-base-80);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
}

.in-room-billing-info-icon i {
	font-size: 8px;
	color: var(--gR-white);
}

.sk-checkbox-wrapper {
	margin-bottom: 24px;
}
</style>

<script>
import { NotificationType } from "@/enums/NotificationType"
import { eventBus, EventBusEvents } from "@/eventBus"
import { PaymentType } from "@reef-os/utils/enums/paymentType"
import mobileResponsivenessMixin from "@/mixins/mobile-responsiveness-mixin"
import squareFormMixin from "@/mixins/square-form-mixin"
import checkoutMixin from "@/mixins/checkout-mixin"
import loaderMixin from "@/mixins/loader-mixin"
import Navbar from "@/components/navbars/Navbar.vue"
import FloatingCartSummary from "@/components/common/FloatingCartSummary.vue"
import PaymentOptions from "@/components/checkout/PaymentOptions.vue"
import CheckoutAdditionalInfo from "@/components/checkout/CheckoutAdditionalInfo.vue"
import CheckoutCartSummary from "@/components/checkout/CheckoutCartSummary.vue"
import DefaultContactForm from "@/components/checkout/DefaultContactForm.vue"
import SkInput from "@/components/common/SkInput.vue"
import SkCheckbox from "@/components/common/SkCheckbox.vue"
import CheckoutDeliveryModeSection from "@/components/checkout/CheckoutDeliveryModeSection.vue"
import CheckoutVoucherCta from "@/components/checkout/CheckoutVoucherCta.vue"
import Footer from "@/components/Footer.vue"
import ItemModal from "@/components/modals/ItemModal.vue"

export default {
	name: "DefaultCheckout",
	mixins: [
		loaderMixin,
		mobileResponsivenessMixin,
		squareFormMixin,
		checkoutMixin
	],
	components: {
		Navbar,
		FloatingCartSummary,
		PaymentOptions,
		CheckoutAdditionalInfo,
		CheckoutCartSummary,
		DefaultContactForm,
		SkInput,
		SkCheckbox,
		CheckoutDeliveryModeSection,
		CheckoutVoucherCta,
		Footer,
		ItemModal
	},
	data: function () {
		return {
			mobileThresholdWidth: 900,
			paymentMethodSelected: null,
			useDeliveryInfo: true,
			alternateFirstName: "",
			alternateLastName: "",
			alternateRoom: ""
		}
	},
	computed: {
		showCreditCardInfo() {
			return this.paymentMethodSelected == PaymentType.CREDIT_CARD
		},
		showInRoomBillingForm() {
			return this.paymentMethodSelected == PaymentType.IN_ROOM_BILLING
		},
		showVoucherCta() {
			return (
				this.$store.state.showVouchers &&
				this.$store.state.cart.length > 0
			)
		},
		paymentMethodObserver() {
			return this.allowedPaymentMethods && this.paymentMethods
		}
	},
	methods: {
		methodsSupported(supportedMethods) {
			if (supportedMethods.applePay) {
				this.applePaySupported = supportedMethods.applePay === true
			}
			if (supportedMethods.googlePay) {
				this.googlePaySupported = supportedMethods.googlePay === true
			}
		},
		paymentMethodClicked(id) {
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "Payment Method Set",
				data: {
					id: id
				}
			})
			this.paymentMethodSelected = id
		},
		resetInputValidtion(inputRef) {
			if (inputRef) {
				inputRef.resetValidation()
			}
		},
		validate() {
			let invalidInput = this.$refs.defaultContactForm.validate()
			if (!invalidInput && !this.useDeliveryInfo) {
				let trackingData = null
				const alternateFirstnameInvalid =
					!this.$refs.alternateFirstNameInput.validate()
				const alternateLastnameInvalid =
					!this.$refs.alternateLastNameInput.validate()
				const alternateRoomInvalid =
					!this.$refs.alternateRoomInput.validate()
				if (alternateFirstnameInvalid) {
					invalidInput = this.$refs.alternateFirstNameInput
					trackingData = {
						reason: "Invalid Alternate First Name",
						alternate_first_name: this.alternateFirstName
					}
				} else if (alternateLastnameInvalid) {
					invalidInput = this.$refs.alternateLastNameInput
					trackingData = {
						reason: "Invalid Alternate Last Name",
						alternate_last_name: this.alternateLastName
					}
				} else if (alternateRoomInvalid) {
					invalidInput = this.$refs.alternateRoomInput
					trackingData = {
						reason: "Invalid Alternate Room",
						alternate_rool: this.alternateRoom
					}
				}
				if (invalidInput) {
					this.$store.dispatch("sendMixpanelEvent", {
						msg: "In Room Billing Form Invalid",
						data: trackingData
					})
					this.focusInput(invalidInput)
				}
			}
			return !invalidInput
		},
		focusInput(input) {
			if (this.$refs.checkoutContent) {
				this.$refs.checkoutContent.scroll({
					top:
						input.$el.offsetTop -
						this.$refs.checkoutContent.offsetTop -
						15,
					behavior: "smooth"
				})
				setTimeout(() => {
					input.focusInput()
				}, 500)
			} else {
				input.focusInput()
			}
		},
		checkoutClicked() {
			const valid = this.validate()
			if (!valid) {
				return
			}

			if (this.$store.getters.totalCheckoutPrice <= 0) {
				if (!this.$store.state.inPurchaseState) {
					this.$store.dispatch("sendMixpanelEvent", {
						msg: "Click Purchase",
						data: {
							type: "Voucher"
						}
					})
					this.toggleLoader(true)
					this.$store.dispatch("processVoucherOrder")
				}
				return
			}
			switch (this.paymentMethodSelected) {
				case PaymentType.CREDIT_CARD: {
					this.clickPurchase()
					break
				}
				case PaymentType.GOOGLE_PAY: {
					this.$refs.paymentOptions.$refs.googlePayBtn.click()
					break
				}
				case PaymentType.APPLE_PAY: {
					this.$refs.paymentOptions.$refs.applePayBtn.click()
					break
				}
				case PaymentType.IN_ROOM_BILLING: {
					if (!this.$store.state.inPurchaseState) {
						this.$store.dispatch("sendMixpanelEvent", {
							msg: "Click Purchase",
							data: {
								type: "In Room Billing"
							}
						})
						this.toggleLoader(true)
						const payload = {}
						if (!this.useDeliveryInfo) {
							payload.alternateFirstName = this.alternateFirstName
							payload.alternateLastName = this.alternateLastName
							payload.alternateRoom = this.alternateRoom
						}
						this.$store.dispatch(
							"processInRoomBillingOrder",
							payload
						)
					}
					break
				}
				default: {
					eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
						message: "Please select a payment option",
						type: NotificationType.WARNING
					})
					throw "Missing payment option"
				}
			}
		},
		onCardNonceError(errors) {
			let errorMsg = errors[0].message
			eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
				message: errorMsg,
				type: NotificationType.ERROR
			})
		},
		inputEventReceived(inputEvent) {
			switch (inputEvent.eventType) {
				case "errorClassAdded":
					this.toggleLoader(false)
					break
			}
		},
		beforeRouteUpdate: function (to) {
			this.detectItemModalRoute(to)
		},
		openCartSummary() {
			this.$refs.cartSummary.open()
		}
	},
	watch: {
		showInRoomBillingForm(newVal) {
			if (newVal) {
				this.useDeliveryInfo = true
				this.alternateFirstName = this.$store.state.user.firstname
				this.alternateLastName = this.$store.state.user.lastname
				this.alternateRoom =
					this.$store.getters.table != undefined &&
					this.$store.getters.table != null
						? `${this.$store.getters.table}`
						: ""
			}
		},
		paymentMethodObserver: {
			immediate: true,
			handler() {
				this.setDefaultPaymentMethod()
			}
		}
	}
}
</script>